import React from 'react';

// Heading Component
export type HeadingLevel = 'display' | 'x-large' | 'large' | 'medium' | 'small';

export interface HeadingProps {
  level?: HeadingLevel;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Heading Component
 *
 * Usage:
 * <Heading level="display">Main Heading</Heading>
 * <Heading level="large" as="h2">Section Heading</Heading>
 */
export function Heading({ level = 'large', as, children, className = '' }: HeadingProps) {
  const Tag =
    as ||
    (level === 'display'
      ? 'h1'
      : level === 'x-large'
        ? 'h2'
        : level === 'large'
          ? 'h3'
          : level === 'medium'
            ? 'h4'
            : 'h5');

  const classes = [`kern-heading-${level}`, className].filter(Boolean).join(' ');

  return <Tag className={classes}>{children}</Tag>;
}

// Title Component
export type TitleSize = 'small' | 'default' | 'large';

export interface TitleProps {
  size?: TitleSize;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Title Component
 *
 * Usage:
 * <Title>Default Title</Title>
 * <Title size="large">Large Title</Title>
 */
export function Title({ size = 'default', as: Tag = 'h2', children, className = '' }: TitleProps) {
  const classes = ['kern-title', size !== 'default' && `kern-title--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return <Tag className={classes}>{children}</Tag>;
}

// Body Component
export type BodySize = 'small' | 'default' | 'large';

export interface BodyProps {
  size?: BodySize;
  bold?: boolean;
  muted?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Body Component
 *
 * Usage:
 * <Body>Regular body text</Body>
 * <Body size="large" bold>Large bold text</Body>
 * <Body size="small" muted>Small muted text</Body>
 */
export function Body({
  size = 'default',
  bold = false,
  muted = false,
  children,
  className = '',
}: BodyProps) {
  const classes = [
    'kern-body',
    size !== 'default' && `kern-body--${size}`,
    bold && 'kern-body--bold',
    muted && 'kern-body--muted',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <p className={classes}>{children}</p>;
}

// Label Component
export type LabelSize = 'small' | 'default' | 'large';

export interface LabelProps {
  size?: LabelSize;
  htmlFor?: string;
  optional?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Label Component
 *
 * Usage:
 * <Label htmlFor="input-name">Name</Label>
 * <Label size="large" optional>Optional Field</Label>
 */
export function Label({
  size = 'default',
  htmlFor,
  optional = false,
  children,
  className = '',
}: LabelProps) {
  const classes = ['kern-label', size !== 'default' && `kern-label--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={classes} htmlFor={htmlFor}>
      {children}
      {optional && <span className="kern-label__optional"> - Optional</span>}
    </label>
  );
}

// Preline Component
export interface PrelineProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Preline Component
 *
 * Usage:
 * <Preline>Category</Preline>
 */
export function Preline({ children, className = '' }: PrelineProps) {
  const classes = ['kern-preline', className].filter(Boolean).join(' ');
  return <p className={classes}>{children}</p>;
}

// Subline Component
export interface SublineProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * KERN Subline Component
 *
 * Usage:
 * <Subline>Subtitle text</Subline>
 */
export function Subline({ children, className = '' }: SublineProps) {
  const classes = ['kern-subline', className].filter(Boolean).join(' ');
  return <p className={classes}>{children}</p>;
}

export default {
  Heading,
  Title,
  Body,
  Label,
  Preline,
  Subline,
};
